import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { startLenis, stopLenis } from "../lib/lenis";

const ease = [0.16, 1, 0.3, 1];

// Shared premium lightbox — used by SelectedWork and the /projects archive.
const WorkLightbox = ({ project, index, total, onClose }) => {
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setImgIdx(0);
  }, [project]);

  const step = useCallback(
    (dir) => {
      if (!project) return;
      setImgIdx((i) => (i + dir + project.images.length) % project.images.length);
    },
    [project]
  );

  // Scroll lock + keyboard navigation while open.
  useEffect(() => {
    if (!project) return;
    stopLenis();
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      startLenis();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [project, onClose, step]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          key="lightbox"
          data-testid="work-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease }}
          className="fixed inset-0 z-[80] flex flex-col bg-black/95 backdrop-blur-md"
          onClick={onClose}
        >
          <div
            className="flex items-center justify-between px-6 md:px-12 h-20 shrink-0 border-b border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">
              {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
            <button
              onClick={onClose}
              data-testid="lightbox-close"
              aria-label="Close project"
              className="text-white/60 hover:text-white transition-colors"
            >
              <X size={24} strokeWidth={1.25} />
            </button>
          </div>

          <div
            className="relative flex-1 min-h-0 flex items-center justify-center px-6 md:px-24 py-8"
            onClick={(e) => e.stopPropagation()}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={`${index}-${imgIdx}`}
                initial={{ opacity: 0, scale: 0.985 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.01 }}
                transition={{ duration: 0.6, ease }}
                className="h-full w-full flex items-center justify-center"
              >
                {project.images[imgIdx].endsWith(".mp4") ? (
                  <video
                    src={project.images[imgIdx]}
                    data-testid="lightbox-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <img
                    src={project.images[imgIdx]}
                    alt={project.title}
                    data-testid="lightbox-image"
                    className="h-full w-full object-contain"
                  />
                )}
              </motion.div>
            </AnimatePresence>

            {project.images.length > 1 && (
              <>
                <button
                  onClick={() => step(-1)}
                  data-testid="lightbox-prev"
                  aria-label="Previous image"
                  className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 border border-white/20 p-3 text-white/60 hover:text-white hover:border-white transition-colors"
                >
                  <ArrowLeft size={18} strokeWidth={1.25} />
                </button>
                <button
                  onClick={() => step(1)}
                  data-testid="lightbox-next"
                  aria-label="Next image"
                  className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 border border-white/20 p-3 text-white/60 hover:text-white hover:border-white transition-colors"
                >
                  <ArrowRight size={18} strokeWidth={1.25} />
                </button>
              </>
            )}
          </div>

          <div
            className="shrink-0 border-t border-white/10 px-6 md:px-12 py-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3
                className="font-heading text-xl md:text-2xl font-light text-white"
                data-testid="lightbox-title"
              >
                {project.title}
              </h3>
              <p className="mt-1 text-sm font-light text-white/50 max-w-xl">
                {project.desc}
              </p>
            </div>
            <div className="flex items-center gap-6 text-[10px] uppercase tracking-[0.3em] text-white/40">
              <span data-testid="lightbox-tags">{project.tags.join(" · ")}</span>
              <span>{project.location}</span>
              {project.images.length > 1 && (
                <span data-testid="lightbox-counter">
                  {imgIdx + 1} / {project.images.length}
                </span>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WorkLightbox;
